import type {ICommentThreadData, IDocumentData} from "@buildwithflux/models";
import {useShallow} from "zustand/react/shallow";

import {useFluxServices} from "../../../../injection/hooks";
import {select} from "../selectors";

/**
 * Hook to get all document comment threads
 * Uses shallow comparison to prevent unnecessary re-renders
 */
export const useDocumentCommentThreads = (): IDocumentData["comment_threads"] | undefined => {
    return useFluxServices().useDocumentStore(useShallow(select.document.commentThreads));
};

/**
 * Hook to get a specific comment thread by UID
 * Uses shallow comparison to prevent unnecessary re-renders
 */
export const useCommentThreadByUid = (uid: string): ICommentThreadData | undefined => {
    return useFluxServices().useDocumentStore(useShallow(select.comments.byUid(uid)));
};

/**
 * Hook to get the UID of a comment thread
 */
export const useCommentThreadUid = (uid: string): string | undefined => {
    return useFluxServices().useDocumentStore(select.comments.uidByUid(uid));
};

/**
 * Hook to get the reference number of a comment thread
 */
export const useCommentThreadReference = (uid: string): number | undefined => {
    return useFluxServices().useDocumentStore(select.comments.referenceByUid(uid));
};

/**
 * Hook to get the owner UID of a comment thread
 */
export const useCommentThreadOwnerUid = (uid: string): string | undefined => {
    return useFluxServices().useDocumentStore(select.comments.ownerUidByUid(uid));
};

/**
 * Hook to get the title of a comment thread
 */
export const useCommentThreadTitle = (uid: string): string | undefined => {
    return useFluxServices().useDocumentStore(select.comments.titleByUid(uid));
};

/**
 * Hook to get the comment count of a comment thread
 */
export const useCommentThreadCommentCount = (uid: string): number | undefined => {
    return useFluxServices().useDocumentStore(select.comments.commentCountByUid(uid));
};

/**
 * Hook to get the anchor of a comment thread
 * Uses shallow comparison to prevent unnecessary re-renders
 */
export const useCommentThreadAnchor = (uid: string): ICommentThreadData["anchor"] | undefined => {
    return useFluxServices().useDocumentStore(useShallow(select.comments.anchorByUid(uid)));
};

/**
 * Hook to get the resolved status of a comment thread
 */
export const useCommentThreadResolved = (uid: string): boolean | undefined => {
    return useFluxServices().useDocumentStore(select.comments.resolvedByUid(uid));
};

/**
 * Hook to get the pinned status of a comment thread
 */
export const useCommentThreadPinned = (uid: string): boolean | undefined => {
    return useFluxServices().useDocumentStore(select.comments.pinnedByUid(uid));
};

/**
 * Hook to get the count of unresolved PCB comment threads
 */
export const useCommentThreadsCountPcb = (): number => {
    return useFluxServices().useDocumentStore(select.comments.unresolvedPcbCount);
};

/**
 * Hook to get the count of unresolved schematic comment threads
 */
export const useCommentThreadsCountSchematic = (): number => {
    return useFluxServices().useDocumentStore(select.comments.unresolvedSchematicCount);
};

/**
 * Hook to get PCB 2D comment thread UIDs
 * Uses shallow comparison to prevent unnecessary re-renders
 */
export const usePcb2dCommentThreadUids = (): string[] => {
    return useFluxServices().useDocumentStore(useShallow(select.comments.pcb2dUids));
};

/**
 * Hook to get pinned schematic comment thread UIDs
 * Uses shallow comparison to prevent unnecessary re-renders
 * Note: Shows all comment threads to crawlers for SEO purposes
 */
export const usePinnedSchematicCommentThreadUids = (): string[] => {
    return useFluxServices().useDocumentStore(useShallow(select.comments.pinnedSchematicUids));
};
